import * as _ from 'lodash';

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { StarRatingClickEvent } from './models/starRatingClickEvent';

@Component({
  selector: 'app-star-rating',
  templateUrl: './star-rating.component.html',
  styleUrls: ['./star-rating.component.scss'],
})
export class StarRatingComponent implements OnInit {
  hovering: boolean = false;
  hoverValue: number = 0;

  @Input()
  hoverEnabled: boolean = false;

  @Input()
  disabled: boolean = false;

  @Input()
  rating: number = 0;

  @Input()
  numOfStars: number = 5;

  @Input()
  showEmpty: boolean = true;

  @Input()
  readOnly: boolean = false;

  @Input()
  staticColor: string = 'ok';

  @Input()
  labelPosition: string = 'left';

  @Input()
  labelText: string = '';

  @Output()
  starClickChange: EventEmitter<StarRatingClickEvent> = new EventEmitter<StarRatingClickEvent>();

  @Output()
  starHoverChange: EventEmitter<StarRatingClickEvent> = new EventEmitter<StarRatingClickEvent>();

  get componentClassNames(): string {
    let classNames = new Array<string>();
    if (this.isHalf) classNames.push('half');

    let value = this.floor;
    if (this.hovering) value = this.hoverValue;
    classNames.push(`value-${value}`);

    let color = this.staticColorClassNames;
    if (color) classNames.push(color);

    classNames.push(this.labelPositionClassNames);

    classNames.push(`label-${this.labelText ? 'visible' : 'hidden'}`);

    if (this.disabled) classNames.push('disabled');

    if (!this.readOnly) classNames.push('editable');

    return classNames.join(' ');
  }

  get stars(): Array<number> {
    let numberOfStars = this.showEmpty ? this.numOfStars : this.floor;
    let offset = 1 + (this.isHalf && !this.showEmpty ? 1 : 0);
    return _.range(1, numberOfStars + offset);
  }

  private get staticColorClassNames(): string {
    if (!this.staticColor) return null;

    let valid = ['default', 'positive', 'ok', 'negative'];
    let position = this.staticColor.toLowerCase().trim();
    return valid.includes(position) ? `color-${position}` : null;
  }

  private get labelPositionClassNames(): string {
    let valid = ['right', 'top', 'left', 'bottom'];
    let position = this.labelPosition.toLowerCase();
    return valid.includes(position) ? `label-${position}` : 'label-left';
  }

  private get floor(): number {
    return Math.floor(this.rating);
  }

  private get isHalf(): boolean {
    return this.floor !== this.rating;
  }

  constructor() {}

  ngOnInit(): void {}

  public onStopHover() {
    if (this.hoverEnabled && this.interactionPossible()) {
      this.hoverValue = this.rating;
      this.hovering = false;
    }
  }

  public onStartHover(index) {
    if (this.hoverEnabled && this.interactionPossible()) {
      this.hovering = true;
      this.hoverValue = index;

      this.starHoverChange.emit({
        rating: index,
      });
    }
  }

  /**ACCESSIBILITY **/

  //Keyboard events
  public onKeyDown(event: KeyboardEvent) {
    if (!this.interactionPossible()) {
      return;
    }

    const handlers: any = {
      //Decrement
      Minus: () => this.decrement(),
      ArrowDown: () => this.decrement(),
      ArrowLeft: () => this.decrement(),

      //Increment
      Plus: () => this.increment(),
      ArrowRight: () => this.increment(),
      ArrowUp: () => this.increment(),

      //Reset
      Backspace: () => this.reset(),
      Delete: () => this.reset(),
      Digit0: () => this.reset(),
    };

    const handleDigits = (eventCode: string): void => {
      const dStr = 'Digit';
      const digit: number = parseInt(eventCode.substr(dStr.length, eventCode.length - 1), 10);
      this.rating = digit;
    };

    if (handlers[event['code']] || StarRatingComponent.isDigitKeyEventCode(event['code'])) {
      if (StarRatingComponent.isDigitKeyEventCode(event['code'])) {
        handleDigits(event['code']);
      } else {
        handlers[event['code']]();
      }
      event.preventDefault();
      event.stopPropagation();
    }
  }

  public setValue(index: number) {
    if (this.interactionPossible()) {
      if (this.floor === index) {
        this.rating = 0;
      } else {
        this.rating = index;
      }

      this.starClickChange.emit({
        rating: this.rating,
      });
    }
  }

  private interactionPossible() {
    return !this.readOnly && !this.disabled;
  }

  private reset() {
    this.rating = 0;
  }

  private decrement() {
    if (this.rating > 0) {
      this.rating = this.rating - 1;
    }
  }

  private increment() {
    if (this.rating < this.numOfStars) {
      this.rating = this.rating + 1;
    }
  }

  /*
   * isDigitKeyEventCode
   * detects digit key event sodes
   * @param eventCode
   * @returns {boolean}
   */
  static isDigitKeyEventCode(eventCode: string): boolean {
    return eventCode.indexOf('Digit') === 0;
  }
}
